import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { Container, Stack, Text, Title, Button, Paper, Loader, Center, Badge, Group, Divider } from '@mantine/core'
import { IconCreditCard, IconCheck, IconUsers, IconPlayerPlay } from '@tabler/icons-react'
import { paymentService } from '../services/paymentService'
import { isAuthenticatedAtom } from '../atoms/authAtoms'

export default function EnrollmentPage() {
  const { groupName } = useParams()
  const navigate = useNavigate()
  const [isAuthenticated] = useAtom(isAuthenticatedAtom)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [error, setError] = useState('')

  const { data: enrollment, isLoading } = useQuery({
    queryKey: ['enrollment', groupName],
    queryFn: () => paymentService.getEnrollmentInfo(groupName),
    enabled: !!groupName,
  })

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      sessionStorage.setItem('bw_returnTo', `/enroll/${groupName}`)
      navigate('/login')
      return
    }

    setCheckoutLoading(true)
    setError('')

    try {
      const { url } = await paymentService.createCheckout(enrollment.id)
      window.location.href = url
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to start checkout'
      setError(message)
      setCheckoutLoading(false)
    }
  }

  if (isLoading) {
    return (
      <Center py="xl" style={{ minHeight: '60vh' }}>
        <Loader size="lg" color="cyan" />
      </Center>
    )
  }

  if (!enrollment) {
    return (
      <Center py="xl" style={{ minHeight: '60vh' }}>
        <Text c="dimmed">Enrollment not found.</Text>
      </Center>
    )
  }

  const price = enrollment.price_cents
    ? `$${(enrollment.price_cents / 100).toFixed(2)}`
    : null

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        <Stack gap="xs" align="center">
          <Title order={2} ff="Orbitron, sans-serif" c="cyan.4" ta="center">
            {enrollment.description || enrollment.name}
          </Title>
          {price && (
            <Text size="xl" fw={700} c="white" ta="center">
              {price} <Text span size="sm" c="dimmed">one-time</Text>
            </Text>
          )}
        </Stack>

        {enrollment.stories?.length > 0 && (
          <Paper p="lg" className="glass">
            <Text fw={600} size="sm" c="cyan.3" mb="sm" ff="Orbitron, sans-serif">
              Included Stories
            </Text>
            <Stack gap="sm">
              {enrollment.stories.map((story) => (
                <Group key={story.id} gap="sm">
                  <IconPlayerPlay size={16} color="var(--mantine-color-cyan-5)" />
                  <Text size="sm" style={{ flex: 1 }}>{story.name}</Text>
                  {story.characters_count > 0 && (
                    <Badge size="sm" variant="light" color="teal" leftSection={<IconUsers size={12} />}>
                      {story.characters_count}
                    </Badge>
                  )}
                </Group>
              ))}
            </Stack>
          </Paper>
        )}

        <Divider color="rgba(0, 255, 255, 0.1)" />

        {error && (
          <Text c="red" size="sm" ta="center">{error}</Text>
        )}

        {enrollment.enrolled ? (
          <Stack align="center" gap="sm">
            <Badge size="lg" color="green" leftSection={<IconCheck size={14} />}>
              Enrolled
            </Badge>
            <Text c="dimmed" size="sm" ta="center">
              You have access to all stories in this course.
            </Text>
            <Button
              size="lg"
              leftSection={<IconPlayerPlay size={20} />}
              onClick={() => navigate('/')}
              className="glow-cyan"
            >
              Browse Stories
            </Button>
          </Stack>
        ) : (
          <Button
            size="lg"
            leftSection={<IconCreditCard size={20} />}
            onClick={handleEnroll}
            loading={checkoutLoading}
            className="glow-cyan"
            style={{ alignSelf: 'center' }}
          >
            {isAuthenticated ? 'Enroll Now' : 'Sign In to Enroll'}
          </Button>
        )}
      </Stack>
    </Container>
  )
}
